//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// The single source of truth for compensation maths. Lives in the contract
// package so the server triggers and the UI cannot drift apart.
//
//   Monthly salary is always `base` — KPI never changes it.
//   quarterlyBonus = base × (bonusPct/100) × 3 × (kpi/100)
//   Spot bonus is discretionary and never guaranteed.

/** @public */
export const KPI_MIN = 70

/** @public */
export const KPI_MAX = 120

/** @public */
export const MONTHS_PER_QUARTER = 3

// ---------------------------------------------------------------------------
// Exact decimal arithmetic.
//
// Money is decimal and binary floats are not: $2 349.70 × 12.5% × 3 × 1.2 is
// exactly 1 057.365, but as a float product it comes out a hair under, so it
// was rounded DOWN to 1 057.36. Products and quotients are therefore worked
// out on scaled integers and rounded half away from zero (commercial
// rounding), and sums are added in whole cents / tiyin.
// ---------------------------------------------------------------------------

/** Decimal places a number is read at: enough for money, rates, % and KPI. */
const DECIMALS = 6

/**
 * n × 10^6 as an integer, from its decimal expansion. toFixed gives the
 * correctly rounded expansion of the double, so a value typed as 2349.7 is
 * read back as exactly 2349.700000, and float noise (0.1 + 0.2) disappears.
 */
function scaled (n: number): bigint {
  const s = n.toFixed(DECIMALS)
  const negative = s.startsWith('-')
  const [int, frac] = (negative ? s.slice(1) : s).split('.')
  const value = BigInt(int + frac)
  return negative ? -value : value
}

const BIG_0 = BigInt(0)
const BIG_2 = BigInt(2)
const BIG_10 = BigInt(10)

/**
 * 10^n. By multiplication, not `**`: a build tool that lowers the exponent
 * operator to Math.pow would break it for big integers.
 */
const POWERS: bigint[] = [BigInt(1)]
function pow10 (n: number): bigint {
  while (POWERS.length <= n) POWERS.push(POWERS[POWERS.length - 1] * BIG_10)
  return POWERS[n]
}

/** num ÷ den as an integer, rounded half away from zero. */
function divRound (num: bigint, den: bigint): bigint {
  const negative = num < BIG_0 !== den < BIG_0
  const a = num < BIG_0 ? -num : num
  const b = den < BIG_0 ? -den : den
  const q = (BIG_2 * a + b) / (BIG_2 * b)
  return negative ? -q : q
}

/** Within the range where the scaled-integer route is defined. */
function workable (n: number): boolean {
  return Number.isFinite(n) && Math.abs(n) < 1e15
}

/** Hundredths as a plain number, without a negative zero. */
function fromHundredths (h: bigint): number {
  const n = Number(h) / 100
  return n === 0 ? 0 : n
}

/**
 * @public
 * The product of `factors`, divided by `divisor`, to 2 decimals: exact decimal
 * arithmetic, rounded half away from zero. 0 when anything is not a number.
 */
export function decimalProduct (factors: number[], divisor: number = 1): number {
  if (!factors.every(workable) || !workable(divisor) || divisor === 0) return 0
  const whole = wholeProduct(factors, divisor)
  if (whole !== undefined) return whole
  let num = BigInt(1)
  for (const f of factors) num *= scaled(f)
  // num carries 6 decimals per factor and the divisor 6: shift whichever side
  // brings the quotient to hundredths.
  let den = scaled(divisor)
  const shift = DECIMALS * factors.length - DECIMALS - 2
  if (shift >= 0) den *= pow10(shift)
  else num *= pow10(-shift)
  return fromHundredths(divRound(num, den))
}

/**
 * The same result without big integers when every number is whole and the
 * product is small enough to be exact as a double: most salaries, rates and
 * scores are, and pages re-run the engine on every change.
 */
function wholeProduct (factors: number[], divisor: number): number | undefined {
  if (!Number.isInteger(divisor) || divisor < 0) return undefined
  let product = 100
  for (const f of factors) {
    if (!Number.isInteger(f) || f < 0) return undefined
    product *= f
    if (product > Number.MAX_SAFE_INTEGER) return undefined
  }
  const quotient = Math.floor(product / divisor)
  const remainder = product - quotient * divisor
  const hundredths = remainder * 2 >= divisor ? quotient + 1 : quotient
  return hundredths / 100
}

/**
 * @public
 * a ÷ b to 2 decimals, exact, rounded half away from zero. 0 when b is 0.
 */
export function decimalQuotient (a: number, b: number): number {
  if (!workable(a) || !workable(b) || b === 0) return 0
  return fromHundredths(divRound(scaled(a) * BigInt(100), scaled(b)))
}

/**
 * @public
 * A number in whole hundredths (cents, tiyin), rounded half away from zero.
 * Sums and differences of money are exact on these.
 */
export function toMinor (n: number): number {
  if (!workable(n)) return 0
  // A whole amount is its hundredths exactly.
  if (Number.isInteger(n) && Math.abs(n) < 9e13) return n === 0 ? 0 : n * 100
  const h = Number(divRound(scaled(n), pow10(DECIMALS - 2)))
  return h === 0 ? 0 : h
}

/** @public */
export function round2 (n: number): number {
  if (!Number.isFinite(n)) return 0
  if (!workable(n)) return Math.round(n * 100) / 100
  return fromHundredths(divRound(scaled(n), pow10(DECIMALS - 2)))
}

/**
 * @public
 * The sum of money amounts, added in whole hundredths so that no float error
 * builds up however many there are.
 */
export function sumMoney (values: Iterable<number>): number {
  let total = 0
  for (const v of values) total += toMinor(v)
  const n = total / 100
  return n === 0 ? 0 : n
}

/** @public Clamp a KPI score into the valid 70–120 band. */
export function clampKpi (kpi: number): number {
  if (!Number.isFinite(kpi)) return KPI_MIN
  return Math.min(KPI_MAX, Math.max(KPI_MIN, kpi))
}

/** @public KPI score → multiplier. 70 → 0.7, 100 → 1.0, 120 → 1.2 */
export function kpiFactor (kpi: number): number {
  return clampKpi(kpi) / 100
}

/** @public Bonus percentage after KPI scaling. e.g. (8, 120) → 9.6 */
export function effectiveBonusPct (bonusPct: number, kpi: number): number {
  return decimalProduct([bonusPct, clampKpi(kpi)], 100)
}

/**
 * @public
 * Quarterly bonus, paid at quarter end.
 * `base × (bonusPct/100) × 3 × (kpi/100)` — e.g. (1000, 8, 120) → 288.
 */
export function computeQuarterlyBonus (baseSalaryUsd: number, bonusPct: number, kpi: number): number {
  if (!Number.isFinite(baseSalaryUsd) || !Number.isFinite(bonusPct)) return 0
  // base × pct% × 3 × kpi%, exactly: the two percentages make the ÷ 10 000.
  return decimalProduct([baseSalaryUsd, bonusPct, MONTHS_PER_QUARTER, clampKpi(kpi)], 10000)
}

/** @public Which quarter a 1-based month belongs to. */
export function quarterOfMonth (month: number): number {
  return Math.floor((month - 1) / MONTHS_PER_QUARTER) + 1
}

/** @public True when a 1-based month closes a quarter (3, 6, 9, 12). */
export function isQuarterEnd (month: number): boolean {
  return month % MONTHS_PER_QUARTER === 0
}

/**
 * @public
 * The USD part of a month's pay: base salary every month, plus the quarterly
 * bonus in quarter-closing months. Premiya is so‘m and is added by the
 * payroll engine after conversion (payroll.ts buildMonth), so it is not here.
 */
export function expectedForMonth (baseSalaryUsd: number, bonusPct: number, kpi: number, month: number): number {
  const bonus = isQuarterEnd(month) ? computeQuarterlyBonus(baseSalaryUsd, bonusPct, kpi) : 0
  return sumMoney([baseSalaryUsd, bonus])
}

/**
 * @public
 * Whether a compensation already applied in the given month. Months that end
 * before `effectiveFrom` owe nothing -- otherwise every month before someone
 * was added shows up as unpaid salary.
 */
export function isPayableMonth (effectiveFrom: number | undefined, year: number, month: number): boolean {
  if (effectiveFrom === undefined || effectiveFrom <= 0) return true
  // The month of the pay start in Tashkent time (UTC+5, no daylight saving),
  // like the payroll engine -- not in the device's own time zone.
  const d = new Date(effectiveFrom + 5 * 60 * 60 * 1000)
  return year * 12 + month >= d.getUTCFullYear() * 12 + d.getUTCMonth() + 1
}

/** @public Convert USD to UZS, to the tiyin. */
export function usdToUzs (usd: number, rate: number): number {
  if (!Number.isFinite(usd) || !Number.isFinite(rate)) return 0
  return decimalProduct([usd, rate])
}

/** @public Convert UZS to USD at a rate, to the cent. 0 without a rate. */
export function uzsToUsd (uzs: number, rate: number): number {
  if (!Number.isFinite(uzs) || !Number.isFinite(rate) || rate <= 0) return 0
  return decimalQuotient(uzs, rate)
}

/** @public A level is locked until the company reaches its headcount threshold. */
export function isLevelLocked (unlockAtEmployees: number, activeEmployeeCount: number): boolean {
  if (!Number.isFinite(unlockAtEmployees) || unlockAtEmployees <= 0) return false
  return activeEmployeeCount < unlockAtEmployees
}


/**
 * @public
 * A closed-open window over the ledger: `from` inclusive, `to` exclusive.
 * Exclusive on the right so a month boundary cannot double-count a
 * transaction that lands exactly at midnight.
 */
export interface DateRange {
  from: number
  to: number
}

/**
 * @public
 * The named windows people actually ask for. `all` is deliberately open at
 * both ends rather than "since the first transaction", so it stays correct
 * when older data is imported later.
 */
export type DateRangePreset = 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear' | 'lastYear' | 'all' | 'custom'

/**
 * @public
 * Resolve a preset against a reference date (today, unless testing).
 */
export function resolveRange (preset: DateRangePreset, now: Date = new Date()): DateRange {
  const year = now.getFullYear()
  const month = now.getMonth()

  switch (preset) {
    case 'thisMonth':
      return { from: new Date(year, month, 1).getTime(), to: new Date(year, month + 1, 1).getTime() }
    case 'lastMonth':
      return { from: new Date(year, month - 1, 1).getTime(), to: new Date(year, month, 1).getTime() }
    case 'thisQuarter': {
      const firstMonth = Math.floor(month / 3) * 3
      return {
        from: new Date(year, firstMonth, 1).getTime(),
        to: new Date(year, firstMonth + 3, 1).getTime()
      }
    }
    case 'thisYear':
      return { from: new Date(year, 0, 1).getTime(), to: new Date(year + 1, 0, 1).getTime() }
    case 'lastYear':
      return { from: new Date(year - 1, 0, 1).getTime(), to: new Date(year, 0, 1).getTime() }
    case 'all':
    case 'custom':
    default:
      return { from: 0, to: Number.MAX_SAFE_INTEGER }
  }
}

/**
 * @public
 * Turn the two date inputs into a range. `to` is pushed to the end of its day
 * so picking the same date twice means "that one day" rather than nothing.
 */
export function customRange (fromDate: string, toDate: string): DateRange {
  // "YYYY-MM-DD" from a date input, read as a LOCAL day like the presets in
  // resolveRange (Date.parse reads it as UTC, 5 hours off in Tashkent).
  const localDay = (value: string, plusDays: number): number => {
    const [y, m, d] = value.split('-').map(Number)
    if (!(y > 0 && m > 0 && d > 0)) return NaN
    return new Date(y, m - 1, d + plusDays).getTime()
  }
  const from = localDay(fromDate, 0)
  const to = localDay(toDate, 1)
  return {
    from: Number.isNaN(from) ? 0 : from,
    to: Number.isNaN(to) ? Number.MAX_SAFE_INTEGER : to
  }
}

/**
 * @public
 * Whether a timestamp falls inside the window.
 */
export function inRange (ts: number, range: DateRange): boolean {
  return ts >= range.from && ts < range.to
}
